import { generateJSON, generateHTML } from "@tiptap/html"
import { extensions } from "app/components/Editor/extensions"
import { Note } from "@types"
import { ProcessedEntry, FeedEntry } from "@/types/import"
import { notEmpty } from "@/util/query"
import { generatePostPreview } from "@/util/format"
import { importFromFile } from "util/import-substack"
import {
  ReaderOptions,
  extractFromXml as feedExtractFromXml,
} from "@extractus/feed-extractor"
import { getEmbed, getTweet } from "@/lib/api/public"
import { twitterRegex2 } from "@/app/components/Editor/Twitter/TwitterNode"
import { FeedData as Feed } from "@extractus/feed-extractor"
import { processImage } from "@/app/components/Editor/Image/Image"
import { findFirstImage, uploadThirdPartyImages } from "@/util/image"

const options: ReaderOptions = {
  xmlParserOptions: {
    removeNSPrefix: false,
  },
  getExtraEntryFields: (feedEntry) => {
    console.log("Feed entry", feedEntry)

    let returnValues: Partial<FeedEntry> = {}

    if ("category" in feedEntry) {
      console.log("Category", feedEntry.category)

      if (Array.isArray(feedEntry.category)) {
        const categories = feedEntry.category
          .filter((c) => {
            return (
              "@_domain" in c &&
              ["category", "post_tag"].includes(c["@_domain"]) &&
              "@_nicename" in c
            )
          })
          .map((c) => c["@_nicename"])

        console.log("Categories", categories)

        returnValues = {
          categories: categories,
          ...returnValues,
        }
      }
    }

    if ("wp:post_type" in feedEntry) {
      returnValues = {
        post_type: feedEntry["wp:post_type"] as "post" | "attachment",
        ...returnValues,
      }
    }

    // Wordpress uses this
    if ("content:encoded" in feedEntry) {
      returnValues = {
        content: feedEntry["content:encoded"] as string,
        ...returnValues,
      }
    }

    return returnValues
  },
}

/**
 * Extract feed data from XML content using feed-extractor library.
 * @param xml The XML content to parse
 * @returns Parsed feed or null if extraction fails
 */
const extractFromXml = (xml: string) => {
  try {
    const feed = feedExtractFromXml(xml, options)
    return feed
  } catch (e) {
    console.error("Unable to extract feed", e)
    return null
  }
}

/**
 * Given a file (eg a Wordpress XML export), extract
 * the feed from it.
 */
/**
 * Extract feed entries from an uploaded file (XML or ZIP).
 * @param file The uploaded file to process
 * @param url The user-inputted URL of the blog
 * @returns Promise resolving to an array of feed entries
 */
function extractFeedFromFile(
  file: File,
  url: string
): Promise<Array<FeedEntry>> {
  if (
    !["text/xml", "application/zip", "application/x-zip-compressed"].includes(
      file.type
    )
  ) {
    return Promise.reject(
      "Incorrect filetype: must be .xml or .zip, but was " + file.type
    )
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        // Handle XML files
        if (file.type === "text/xml") {
          const data = reader.result as string
          const feed = extractFromXml(data)
          if (!feed || !feed.entries) {
            reject("Unable to extract feed from XML")
            return
          }
          resolve(feed.entries)
          return
        }
        // Handle ZIP files (Substack exports)
        else {
          console.log("Processing ZIP file", file.name, file.type, file.size)
          try {
            // For ZIP files, we need the entire File object
            const feed = await importFromFile(file, url)

            if (!feed) {
              reject("Unable to extract feed from ZIP file")
              return
            }

            if (feed.length === 0) {
              reject(
                "No posts found in the ZIP file. Ensure it's a valid Substack export."
              )
              return
            }

            console.log(
              "Successfully processed",
              feed.length,
              "entries from ZIP"
            )
            resolve(feed)
          } catch (zipError) {
            console.error("Error processing ZIP file:", zipError)
            reject(
              typeof zipError === "string"
                ? zipError
                : "Error processing Substack export"
            )
          }
        }
      } catch (error) {
        console.error("Error in file reader onload", error)
        reject(error instanceof Error ? error.message : "Error processing file")
      }
    }
    reader.onerror = () => {
      reject(reader.error)
    }
    // For ZIP files, we need to read as ArrayBuffer instead of text
    if (file.type.includes("zip")) {
      reader.readAsArrayBuffer(file)
    } else {
      reader.readAsText(file)
    }
  })
}

/**
 * Filter out any erroneous entries (eg all attachments or posts that were not published)
 */
function processEntries(entries: Array<FeedEntry>): Array<ProcessedEntry> {
  if (!entries) return []

  // Log what we're starting with
  console.log(`Processing ${entries.length} entries`)

  // First filter entries with required fields
  const validEntries = entries.filter((e) => {
    const hasContent = !!e.content
    const hasTitle = !!e.title
    const hasPublished = !!e.published
    const isPostType = "post_type" in e ? e.post_type === "post" : true

    const isValid = hasContent && hasTitle && hasPublished && isPostType
    if (!isValid) {
      console.log(
        `Filtering out entry: missing ${!hasContent ? "content " : ""}${!hasTitle ? "title " : ""}${!hasPublished ? "published date " : ""}${!isPostType ? "wrong post type" : ""}`
      )
      if (!hasContent) {
        console.log(
          `Entry with title "${e.title || "unknown"}" is missing content`
        )
      }
    }

    return isValid
  })

  console.log(
    `Found ${validEntries.length} valid entries out of ${entries.length}`
  )

  // Then map to the right format
  return validEntries.map((entry) => {
    const { title, content, subtitle, link, published, categories } = entry

    // Extra verification for content
    if (!content) {
      console.error(
        `Entry "${title}" has no content even though it passed filter`
      )
      // Create a placeholder content to prevent failures
      const placeholderContent = `<p>This post was imported but content could not be retrieved.</p>`
      return {
        title: title || "Untitled Post",
        content: placeholderContent,
        subtitle,
        link,
        published: new Date().toString(),
        categories,
      }
    }

    // Handle different date formats
    let publishedDate = ""
    try {
      if (published instanceof Date) {
        publishedDate = published.toString()
      } else if (typeof published === "string") {
        publishedDate = published
      } else {
        publishedDate = new Date().toString()
      }
    } catch (e) {
      console.error("Error formatting date", e)
      publishedDate = new Date().toString()
    }

    const processed: ProcessedEntry = {
      title: title || "Untitled Post",
      content: content,
      subtitle,
      link,
      published: publishedDate,
      categories,
    }

    // Log some details about the processed entry
    console.log(`Processed entry: ${processed.title?.substring(0, 30)}...`)
    console.log(
      `  Content length: ${processed.content?.length || 0} characters`
    )

    return processed
  })
}

function extractRedirectFromLink(link: string) {
  let url
  if (!link.match(/^[a-zA-Z]+:\/\//)) {
    link = "http://" + link
  }

  try {
    url = new URL(link)
  } catch (e) {
    console.error("Error extracting redirect from link " + link, e)
    return "" // or return a default value, or handle as appropriate
  }

  let pathname = url.pathname

  // Remove leading and trailing slashes, since we need to do a Firestore
  // lookup on the path
  pathname = pathname.replace(/^\/|\/$/g, "")

  // If the patname is empty (or a single slash), just return
  if (pathname === "") return undefined

  return pathname
}

/**
 * Given a processed entry, turn it into a properly formatted note with all defaults set.
 *
 * Note that we don't need to do EVERYTHING here (eg set the slug), since a lot of that
 * logic happens on the server-side when we publish the note.
 */
export /**
 * Converts processed entries into properly formatted notes.
 * @param entries Array of processed blog entries
 * @param setCanonical Whether to set the canonical URL
 * @returns Array of formatted notes ready for import
 */
function createNoteFromProcessedEntry(
  entries: Array<ProcessedEntry>,
  setCanonical?: boolean
): Array<Partial<Note> | undefined> {
  const formattedNotes = entries.map((entry) => {
    if (!entry.content) {
      console.log(`Entry missing content: ${entry.title || "untitled"}`)
      return
    }

    console.log(
      `Processing entry with title: ${entry.title}, content length: ${entry.content.length}`
    )

    try {
      const json = generateJSON(entry.content, extensions)
      const staticHtml = generateHTML(json, extensions).replace(
        /<p[^>]*>(\s*)<\/p>/g,
        "<br>"
      )
      let redirectString
      if (entry.link) {
        redirectString = extractRedirectFromLink(entry.link)
      }

      const note: Partial<Note> = {
        // Set filler cover_img here with the imported cover image URL to be uploaded and replaced later in the import process
        cover_img: entry.coverImageUrl
          ? {
              isHero: true,
              base64: "",
              // Height and width are set as the largest values we use in case this doesn't get replaced because height and width 0 can lead to an error
              img: { src: entry.coverImageUrl, height: 508, width: 285 },
            }
          : undefined,
        title: entry.title,
        subtitle: entry.subtitle,
        json: JSON.stringify(json),
        staticHtml,
        canonicalUrl: setCanonical ? entry.link : undefined,
        publishedAt: Date.parse(entry.published),
        post_preview: generatePostPreview(staticHtml),
        redirectFrom: redirectString ? [redirectString] : undefined,
        categories: entry.categories,
      }

      console.log(`Successfully created note for: ${entry.title}`)
      // Note is now formatted and ready for import

      return note
    } catch (error) {
      console.error(
        `Error processing entry ${entry.title || "untitled"}: `,
        error
      )
      return undefined
    }
  })

  return formattedNotes
}

/**
 * Given a file, extract the feed from it, or if given a feed, skip to
 * process the entries (eg verify data is OK),
 * then create Note objects for each entry.
 *
 * The URL is the user-inputted URL. If this is a custom domain (eg not .substack.com),
 * we need to set the redirectFrom field to the custom domain.
 *
 * Wordpress contains the URL in the actual file, but Substack does not.
 */
/**
 * Primary function to create notes from either an uploaded file or feed data.
 * @param file Uploaded file (if any)
 * @param feed Feed data (if any)
 * @param url Blog URL for redirection setup
 * @returns Array of formatted notes ready for import
 */
export async function createNotesFromFileOrFeed(
  file: File | null,
  feed: Feed | null,
  url: string
): Promise<Array<Partial<Note>> | undefined> {
  if (!file && !feed) {
    console.error("createNotesFromFileOrFeed called without file or feed")
    return
  }

  try {
    console.log(
      "Creating notes from",
      file ? `file (${file.name}, ${file.type})` : "feed",
      "url:",
      url
    )

    // Get entries from file or feed
    const entries = file ? await extractFeedFromFile(file, url) : feed?.entries
    if (!entries) {
      console.error("No entries found in file or feed")
      return
    }

    console.log(
      `Found ${entries.length} raw entries in ${file ? "file" : "feed"}`
    )

    // Process entries to ensure they have required fields
    const processed = processEntries(entries)
    console.log(
      `Processed ${processed.length} valid entries from ${entries.length} total`
    )

    if (processed.length === 0) {
      console.error("No valid entries found after processing")
      throw new Error("No valid entries could be processed from the file/feed")
    }

    // Create notes from processed entries
    const notes = createNoteFromProcessedEntry(processed)
    console.log(`Created ${notes.length} note objects`)

    // Filter out undefined notes
    const filtered = notes.filter(notEmpty)
    console.log(`Filtered to ${filtered.length} valid notes`)

    return filtered
  } catch (error) {
    console.error("Error in createNotesFromFileOrFeed:", error)
    throw error
  }
}

/**
 * Given a Note, we iterate through the JSON to check the Image nodes.
 *
 * If we detect an Image node using a 3p URL, we re-upload it to our own server,
 * and regenerate the JSON and staticHTML of the note.
 *
 * Note that this returns nothing but instead just updates the note by reference.
 */
/**
 * Processes a note to reupload all third-party images to our CDN.
 * Also sets the cover image either from the provided URL or from the first image in the content.
 * @param note The note object to process
 * @param coverImageUrl Optional cover image URL
 */
export async function reuploadImages(
  note: Partial<Note>,
  coverImageUrl?: string
): Promise<void> {
  if (!note.json) return

  // Upload any third-party images in the content
  const content = await uploadThirdPartyImages(
    JSON.parse(note.json),
    processImage
  )
  note.json = JSON.stringify(content)
  note.staticHtml = generateHTML(content, extensions)

  // Process cover image
  let coverImageAttrs
  if (coverImageUrl) {
    // Use provided cover image URL
    coverImageAttrs = await processImage(coverImageUrl)
  } else {
    // Use first image in content as cover
    coverImageAttrs = findFirstImage(content)
  }

  const isImageProcessed =
    !!coverImageAttrs &&
    "blurdataurl" in coverImageAttrs &&
    "src" in coverImageAttrs

  // If the image fails to be processed we set it to undefined to avoid images from external sites
  note.cover_img = isImageProcessed
    ? {
        isHero: !!coverImageUrl,
        base64: coverImageAttrs.blurdataurl,
        img: {
          height: coverImageAttrs.nextheight,
          width: coverImageAttrs.nextwidth,
          src: coverImageAttrs.src,
        },
      }
    : undefined
}

export async function convertLinksIntoLinkEmbeds(note: Partial<Note>) {
  if (!note.json) return
  note.json = JSON.stringify(
    await convertLinkToLinkEmbed(JSON.parse(note.json))
  )
}

/**
 * Iterate through the TipTap JSON and convert lone links to link embeds (or Tweet embeds for Twitter links).
 */
async function convertLinkToLinkEmbed(json: any) {
  const content: any = json.content?.map(async (element: any) => {
    // If we detect a link that is alone and has the URL as the text, we convert it to a link embed
    if (
      element &&
      element.type === "paragraph" &&
      element.content.length == 1 &&
      element.content[0].marks?.find((m: any) => m.type === "link") &&
      element.content[0].text ===
        element.content[0].marks?.find((m: any) => m.type === "link").attrs.href
    ) {
      const url = element.content[0].marks?.find((m: any) => m.type === "link")
        .attrs.href

      console.log(
        "Link detected in post, so converting it to a link embed...",
        element,
        element.content,
        element.content[0],
        url
      )
      try {
        const tweetMatch = url.match(twitterRegex2)
        if (tweetMatch) {
          console.log(
            "Tweet detected in link, so converting it to a tweet embed...",
            tweetMatch
          )
          const tweetUrl = tweetMatch[0]
          const tweetId = tweetMatch[3]

          if (tweetUrl && tweetId) {
            console.log("Fetching Tweet data for Tweet ID", tweetId)
            const data = await getTweet(tweetId)
            console.log("Fetched Tweet data", data)
            if (data)
              return {
                type: "twitter",
                attrs: {
                  tweetId,
                  tweetData: data,
                  tweetUrl,
                },
              }
            else console.warn("Failed to fetch Tweet: ", tweetId)
          }
        }
        console.log("Fetching embed data for URL", url)
        const data = await getEmbed(url)
        console.log("Fetched embed data", data)
        if (data)
          return {
            type: "embedly",
            attrs: {
              src: url,
              format: data.html ? "iframe" : "small",
              data: JSON.stringify(data),
            },
          }
      } catch (e) {
        console.error(`Failed to process link: ${url}`, e)
      }

      return element
    } else if (Array.isArray(element.content))
      return convertLinkToLinkEmbed(element)
    else return element
  })

  return { ...json, content: await Promise.all(content) }
}
